import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import strings from 'utils/strings'
import featuresStrings from 'utils/strings/features-strings'

import yup from 'schemas/validation/feature'
import featureMetaSchema from 'schemas/validation/feature/featureMetaSchema'
import { schemaProp } from 'schemas/validation'
import { keepAwakeLabel as switchLabel } from 'i18n/displayConstants/featureOutputs'

import { Divider, TextInput, Paragraph } from 'components/patterns'
import { ModalCloseButton } from 'components/patterns/Modal'
import Harnesses from 'components/feature/Harnesses'
import FeatureEdit from 'components/feature/FeatureEditor/FeatureEdit'
import ModalErrorContent from 'components/patterns/ModalErrorContent'
import useFeaturePreCreateModalHook from './FeaturePreCreateModal.hook'
import { CreateIcon } from '../FeatureSearch.style'
import {
  StyledFeaturePreCreateModal,
  StyledDescriptionInput,
  StyledModalHeader,
  StyledModalHeaderWrapper,
  StyledModuleNameRadioEditor,
  StyledCollapsibleWidget,
  StyledWidgetHeader,
  StyledBusSwitch,
  InfoIcon,
  SchoolBusMessage,
} from './FeaturePreCreateModal.style'

const collapsibleWidgetHeader = (
  <StyledWidgetHeader>{strings.advanced_settings}</StyledWidgetHeader>
)
const rule = schemaProp(featureMetaSchema)
const FeaturePreCreateModal = ({
  modalIsOpen,
  className: modalClassname,
  toggleModal,
}) => {
  const {
    busMode,
    moduleName,
    description,
    moduleNameIsEditable,
    allDescriptions,
    updateDescription,
    featureId,
    handleCancelCreate,
    initialFeature,
    handleCreate,
    onBusModeChange,
    harnessesMapped,
    isInternalFeature,
    showHarnesses,
    isValidNewFeature,
    isInitial,
    modalError,
    clearModalError,
    ...rest
  } = useFeaturePreCreateModalHook()
  const [isCreatingFeature, setIsCreatingFeature] = useState(false)

  // When the API fails, modalError is set by createModalErrorHandler. Reset
  // isCreatingFeature so the button doesn't carry stale spinner/disabled state
  // if the user closes and reopens the modal after a failed creation attempt.
  useEffect(() => {
    if (modalError) setIsCreatingFeature(false)
  }, [modalError])

  const closeModalAndCancelCreate = () => {
    toggleModal(false)
    handleCancelCreate()
    // Clear the API error so that reopening the modal shows the normal creation
    // UI rather than the error body with the Create button disabled.
    clearModalError()
  }

  const modalHeader = (
    <StyledModalHeaderWrapper>
      <StyledModalHeader type="h3">
        {strings.create_a_feature}
      </StyledModalHeader>
      <ModalCloseButton
        onClick={closeModalAndCancelCreate}
        aria-label={strings.close}
      />
    </StyledModalHeaderWrapper>
  )

  const precreateModalButtons = () => [
    {
      text: strings.cancel,
      type: 'secondary',
      onClick: closeModalAndCancelCreate,
    },
    {
      text: (
        <>
          {!isCreatingFeature && (
            <CreateIcon
              icon="add"
              fill={
                !isValidNewFeature || modalError ? 'slateSecondary' : 'white'
              }
            />
          )}
          {featuresStrings.create_feature}
        </>
      ),
      type: 'primary',
      onClick: () => {
        setIsCreatingFeature(true)
        handleCreate(() => {
          toggleModal(false)
          setIsCreatingFeature(false)
        })
      },
      disabled: !isValidNewFeature || !!modalError,
      loading: isCreatingFeature && !modalError,
    },
  ]

  return (
    <StyledFeaturePreCreateModal
      isOpen={modalIsOpen}
      customButtons={precreateModalButtons()}
      className={`${modalClassname || ''}${
        modalError ? ' modal-error-state' : ''
      }`}
      header={modalHeader}
      size="large"
      noHandler={closeModalAndCancelCreate}
    >
      {modalError ? (
        <ModalErrorContent />
      ) : (
        <>
          <StyledDescriptionInput>
            <TextInput
              rules={rule('description', {
                descriptions: allDescriptions,
              })}
              label={strings.feature_description}
              name={strings.feature_description}
              placeholder={strings.feature_description}
              autoFocus={true}
              onChange={(event) => updateDescription(event, featureId)}
              value={description}
            />
          </StyledDescriptionInput>
          <StyledBusSwitch
            className="bus-type-switch"
            value={busMode}
            onChange={onBusModeChange}
            name={strings.school_bus_mode}
            label={strings.school_bus_mode}
            rules={yup.boolean().nullable()}
            switchLabels={switchLabel}
          />
          <SchoolBusMessage>
            <InfoIcon icon="question_mark_tooltip" fill="outlined" />
            <Paragraph size="small">
              {strings.school_bus_valid_modules_message}
            </Paragraph>
          </SchoolBusMessage>
          <StyledModuleNameRadioEditor
            className="feature-precreate-module-selector"
            moduleNameIsEditable={moduleNameIsEditable || isInitial}
            moduleName={moduleName}
            isInitial={isInitial}
            featureId={featureId}
            busMode={busMode}
            harnessesMapped={harnessesMapped}
          />
          <Divider type="horizontal" />
          <StyledCollapsibleWidget
            header={collapsibleWidgetHeader}
            className="feature-precreate-advanced-settings"
            panelClassName="feature-settings"
            activeKey="0"
          >
            <FeatureEdit
              isPreCreate={true}
              isInternalFeature={isInternalFeature}
              moduleName={moduleName}
              description={description}
              moduleNameIsEditable={moduleNameIsEditable}
              allDescriptions={allDescriptions}
              updateDescription={updateDescription}
              featureId={featureId}
              handleCancelCreate={handleCancelCreate}
              initialFeature={initialFeature}
              handleCreate={handleCreate}
              onBusModeChange={onBusModeChange}
              harnessesMapped={harnessesMapped}
              showHarnesses={showHarnesses}
              {...rest}
            />
            {showHarnesses && <Harnesses isPreCreate />}
          </StyledCollapsibleWidget>
        </>
      )}
    </StyledFeaturePreCreateModal>
  )
}

FeaturePreCreateModal.defaultProps = {
  modalIsOpen: false,
}

FeaturePreCreateModal.propTypes = {
  modalIsOpen: PropTypes.bool,
  validationData: PropTypes.object,
  className: PropTypes.string,
  toggleModal: PropTypes.func,
  setBusTypeFeature: PropTypes.func,
}

export default FeaturePreCreateModal
